import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  List,
  ListItem,
  Typography,
  styled,
  ListItemButton,
  ListItemText,
  Button,
  Menu,
  MenuItem,
  useTheme,
  Popper,
} from '@mui/material';
import { animateScroll as scroll } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Language } from '@mui/icons-material';
import { MdAnalytics } from 'react-icons/md';
import DrawerItem from './DrawerItem';
import { Colors } from '../style/theme';
import useResponsive from '../../hooks/useResponsive';
import colorLogo from '../../Logo/watch-nod.png';

const styles = {
  logoImage: (isLargeScreen) => ({
    width: '100%',
    maxWidth: isLargeScreen ? '140px' : '100px',
    maxHeight: isLargeScreen ? '120px' : '100px',
    objectFit: 'contain',
  }),
};

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  paddingRight: '20px',
  paddingLeft: '10px',
});

const ListMenu = styled(List)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));

const appBarStyles = (scrolling) => ({
  backgroundColor: scrolling ? '#ffffff' : 'transparent',
  width: '100%',
  height: '100px',
});

const buttonStyle = {
  color: Colors.white,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: Colors.blue,
  '&:hover': {
    backgroundColor: Colors.dark_blue,
  },
  borderRadius: '10px',
  textDecoration: 'none',
};

function useNavbarLogic() {
  const [scrolling, setScrolling] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  const scrollToTop = () => {
    scroll.scrollToTop({
      duration: 500,
      smooth: 'easeInOutQuart',
    });
  };

  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== '/') {
      navigate(`/#${sectionId}`);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleItemClick = (index, onClick) => {
    setActiveItem(index);
    if (onClick) onClick();
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const isScrolling = scrollTop > 50;
      setScrolling(isScrolling);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTokenExpired = () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch {
      return true;
    }
  };

  const isLoggedIn =
    !!localStorage.getItem('token') &&
    !!localStorage.getItem('userId') &&
    !!localStorage.getItem('role') &&
    !isTokenExpired();

  return {
    scrolling,
    activeItem,
    navigate,
    theme,
    scrollToTop,
    scrollToSection,
    handleItemClick,
    setScrolling,
    setActiveItem,
    isLoggedIn,
  };
}

function Navbar() {
  const {
    scrolling,
    activeItem,
    navigate,
    theme,
    scrollToTop,
    scrollToSection,
    handleItemClick,
    setScrolling,
    setActiveItem,
    isLoggedIn,
  } = useNavbarLogic();

  const isDesktop = useResponsive('up', 'lg');
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const isResourcesMenuOpen = Boolean(resourcesAnchorEl);
  const handleResourcesOpen = (event) => setResourcesAnchorEl(event.currentTarget);
  const handleResourcesClose = () => setResourcesAnchorEl(null);

  const itemList = [
    {
      text: <strong>Pricing</strong>,
      href: '/#pricing',
      onClick: () => scrollToSection('pricing'),
    },
    {
      text: <strong>Contact Us</strong>,
      href: '/contact',
      onClick: () => navigate('/contact'),
    },
    {
      button: <strong>Login</strong>,
      href: isLoggedIn ? '/dashboard' : '/login',
      onClick: () => (isLoggedIn ? navigate('/dashboard') : navigate('/login')),
    },
    {
      button: <strong>Signup</strong>,
      href: isLoggedIn ? '/dashboard' : '/signup',
      onClick: () => (isLoggedIn ? navigate('/dashboard') : navigate('/signup')),
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolling(scrollTop > 50);

      const sectionPositions = {
        feature: document.getElementById('feature')?.offsetTop,
        about: document.getElementById('about')?.offsetTop,
        testimonial: document.getElementById('testimonial')?.offsetTop,
        pricing: document.getElementById('pricing')?.offsetTop,
      };

      let activeSection = 'home';
      Object.keys(sectionPositions).forEach((section) => {
        if (scrollTop >= (sectionPositions[section] || 0) - 50) {
          activeSection = section;
        }
      });

      const activeIndex = itemList.findIndex(
        (item) => item.text && item.text.toString().toLowerCase().includes(activeSection)
      );

      setActiveItem(activeIndex);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [itemList, setActiveItem]);

  return (
    <AppBar component="nav" position="fixed" sx={appBarStyles(scrolling)} elevation={0}>
      <StyledToolbar disableGutters style={{ 'backgroundColor': 'white' }}>
        <Box
          component="a"
          href="/"
          sx={{ cursor: 'pointer', transition: 'transform 0.3s ease-in-out', textDecoration: 'none' }}
          onClick={(e) => {
            e.preventDefault();
            scrollToTop();
            navigate('/');
          }}
        >
          <img
            src={colorLogo}
            alt="WatchNod"
            title="WatchNod Logo"
            loading="eager"
            style={styles.logoImage(isLargeScreen)}
          />
        </Box>

        {!isDesktop ? (
          <Box sx={{ color: 'black' }}>
            <DrawerItem />
          </Box>
        ) : (
          <ListMenu>
            {/* Services Dropdown */}
            <ListItem
              sx={{ position: 'relative' }}
            >
              <ListItemButton
                component="a"
                href="/#services"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
                disableRipple
                sx={{
                  color: Colors.blue,
                  fontWeight: theme.typography.fontWeightBold,
                  '&:hover': {
                    color: Colors.dark_blue,
                    backgroundColor: 'transparent',
                  },
                  textTransform: 'none',
                  py: 1.5,
                  px: 2,
                  textDecoration: 'none',
                }}
              >
                <ListItemText
                  primary={<strong>Services</strong>}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </ListItem>


            {/* Pricing & Contact */}
            {itemList.slice(0, 2).map((item, index) => {
              const isActive = activeItem === index;
              return (
                <ListItem key={index}>
                  <ListItemButton
                    component="a"
                    href={item.href}
                    disableRipple
                    sx={{
                      color: isActive ? Colors.dark_blue : Colors.blue,
                      fontWeight: theme.typography.fontWeightBold,
                      '&:hover': { color: Colors.dark_blue, backgroundColor: 'transparent' },
                      textDecoration: 'none',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleItemClick(index, item.onClick);
                    }}
                  >
                    <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: true }} />
                  </ListItemButton>
                </ListItem>
              );
            })}

            {/* Resources Dropdown */}
            <ListItem
              onMouseEnter={handleResourcesOpen}
              onMouseLeave={handleResourcesClose}
              sx={{ position: 'relative' }}
            >
              <ListItemButton
                disableRipple
                sx={{
                  color: Colors.blue,
                  fontWeight: theme.typography.fontWeightBold,
                  '&:hover': { color: Colors.dark_blue, backgroundColor: 'transparent' },
                  textTransform: 'none',
                  py: '12px',
                  px: '16px',
                }}
                onClick={handleResourcesOpen}
              >
                <ListItemText primary={<strong>Resources</strong>} primaryTypographyProps={{ noWrap: true }} />
              </ListItemButton>

              <Menu
                anchorEl={resourcesAnchorEl}
                open={isResourcesMenuOpen}
                onClose={handleResourcesClose}
                MenuListProps={{ onMouseLeave: handleResourcesClose }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ mt: 1 }}
              >
                <MenuItem component="a" href="/blog" onClick={(e) => { e.preventDefault(); handleResourcesClose(); navigate('/blog'); }}>Blog</MenuItem>
                <MenuItem component="a" href="/document" onClick={(e) => { e.preventDefault(); handleResourcesClose(); navigate('/document'); }}>Documentation</MenuItem>
                <MenuItem component="a" href="/case-studies" onClick={(e) => { e.preventDefault(); handleResourcesClose(); navigate('/case-studies'); }}>Case Studies</MenuItem>
                <MenuItem component="a" href="/faq" onClick={(e) => { e.preventDefault(); handleResourcesClose(); navigate('/faq'); }}>FAQs</MenuItem>
                <MenuItem component="a" href="/about" onClick={(e) => { e.preventDefault(); handleResourcesClose(); navigate('/about'); }}>About Us</MenuItem>
              </Menu>
            </ListItem>

            {/* Login / Signup Buttons */}
            {itemList.slice(2).map((item, index) => {
              const actualIndex = index + 2;
              return (
                <ListItem key={actualIndex}>
                  <Button
                    component="a"
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleItemClick(actualIndex, item.onClick);
                    }}
                    sx={{ ...buttonStyle, fontWeight: theme.typography.fontWeightBold, textDecoration: 'none' }}
                  >
                    {item.button}
                  </Button>
                </ListItem>
              );
            })}
          </ListMenu>
        )}
      </StyledToolbar>
    </AppBar>
  );
}

export { Navbar };
